import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Anchor } from '@percent/lemonade'
import {
  describeRoleSource,
  viaGroupLabel,
} from '../../utils/describeRoleSource'
import type { EffectiveRoleGroupRef } from '../../types/effectiveRoles.types'
import type { RoleSourcesProps } from './RoleSources.types'
import styles from './RoleSources.module.scss'

// Why a role is held, one reason per line: "via group Payments → Finance team",
// from the subject's own group to the one holding the role, linked when the app
// can open groups; "from Manager".
export function RoleSources({
  sources,
  getGroupHref,
  className,
  testId,
}: RoleSourcesProps) {
  const { t } = useTranslation()
  const viaGroup = viaGroupLabel(t)

  // A group without a name is one the viewer cannot view: its id, unlinked.
  const group = (ref: EffectiveRoleGroupRef) => {
    const href = ref.name ? getGroupHref?.(ref.id) : undefined

    return href ? (
      <Anchor component={Link} to={href} inherit>
        {ref.name}
      </Anchor>
    ) : (
      <span>{ref.name ?? ref.id}</span>
    )
  }

  return (
    <span
      className={[styles.sources, className].filter(Boolean).join(' ')}
      data-testid={testId}
    >
      {sources.map((source, index) => (
        // eslint-disable-next-line react/no-array-index-key -- sources have no id and never reorder
        <span key={index} className={styles.source}>
          {source.type === 'group' ? (
            <>
              {viaGroup}{' '}
              {source.path.map((step, at) => (
                <Fragment key={step.id}>
                  {at === 0 ? (
                    group(step)
                  ) : (
                    <>
                      {' '}
                      <span className={styles.step}>→ {group(step)}</span>
                    </>
                  )}
                </Fragment>
              ))}
            </>
          ) : (
            describeRoleSource(source, t)
          )}
        </span>
      ))}
    </span>
  )
}
